import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Box, Typography } from "@mui/material";
import SectionSlider from "../Reusable/SectionSlider/SectionSlider";
import { sections, sectionPaths } from '../../constants/sectionData';
import ActivityBar from "../Reusable/ActivityBar/ActivityBar";
import pdf from '../../assets/images/fileImage/pdf.png';
import image from '../../assets/images/fileImage/image_icon.png';
import { apiService } from "../../api/apiService";
import ProjectManagementTabs from "../Reusable/utils/ProjectManagementTabs";
import ProjectManagementPopup from "../Reusable/Routine/Popup/ProjectManagementPopup";
import ProjectManagementFileView from "../Reusable/Popup/ProjectManagementFileView";
import ProjectManagementAdminFileView from "../Reusable/Popup/ProjectManagementAdminFileView";
import { getFileImagePath } from "../Reusable/utils/fileUtils";
import { useMsal } from "@azure/msal-react";
import { PublicClientApplication } from "@azure/msal-browser";

interface LocationState {
  management?: string;
  id: any;
  function_l1: any;
  current_project: any;
  file_name: string;
  created_at: any;
  updated_at: any;
  updated_by: any;
  onClick: (id: any, file_name: string, current_project: any, function_l1: any, created_at: any, updated_at: any, updated_by: any) => void;
  status: any;
  reason_to_approve: any;
  reason_to_revert: any;
}

interface GlobalPoliciesUploadProps {
  isAdmin?: boolean;
  isManager?: boolean;
}

interface FilterValues {
  function_l1: string;
  function_l2: string;
  function_l3: string;
  function_l4: string;
}

const verticalTabs = [
  { label: "Project Management", value: "a" },
  {
    label: "Change Management",
    value: "b",
    subTabs: [
      { label: "Risk Assessment", value: "c" },
      { label: "Success Change Management Plan", value: "d" },
    ]
  },
];

const getFileQuestions = (tabValue: string) => {

  switch (tabValue) {
    case "a":
      return "Question : Project management methodologies are followed for complex projects in BSC (as per complexity matrix)";
    case "c":
      return "Question : Change management is applied for complex projects in BSC (as per complexity matrix)";
    case "d":
      return "Question : Change management is applied for complex projects in BSC (as per complexity matrix)";
    default:
      return null;
  }
};


const ProjectManagementUpload: React.FC<GlobalPoliciesUploadProps> = ({ isAdmin, isManager }) => {
  const { instance } = useMsal();

  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState("1");
  const [verticalTabValue, setVerticalTabValue] = useState("a");
  const [openUpload, setOpenUpload] = useState(false);
  const [open, setOpen] = useState(false);
  const [routineData, setRoutineData] = useState<any[]>([]);
  const [isDropdownOpen, setDropdownOpen] = useState(false);
  const management = localStorage.getItem("management") || "Default Location Name";
  const [currentPage, setCurrentPage] = useState(1);
  const [filterValue, setFilterValue] = useState('');
  const [files, setFiles] = useState<LocationState[]>([]);
  const [isPopupOpen, setPopupOpen] = useState(false);
  const [page, setPage] = useState(1);
  const itemsPerPage = 3;

  const handleChangePage = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const tabStatuses: { label: string; value: 'All' | 'Pending' | 'Approved' | 'Reverted'; apiStatus: string | null }[] = [
    { label: 'All', value: 'All', apiStatus: null },
    { label: 'Pending', value: 'Pending', apiStatus: 'Pending' },
    { label: 'Approved', value: 'Approved', apiStatus: 'Approved' },
    { label: 'Reverted', value: 'Reverted', apiStatus: 'Reverted' },
  ];

  const selectedStatus = tabStatuses.find(tab => String(tabStatuses.indexOf(tab) + 1) === tabValue)?.value;
  const filteredRoutineData = selectedStatus === "All" ? routineData : routineData.filter(item => item.status === selectedStatus);
  const paginatedData = filteredRoutineData.slice(startIndex, endIndex);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClosePopup = () => {
    setPopupOpen(false);
  };

  const [filterValues, setFilterValues] = useState<FilterValues>({
    function_l1: '',
    function_l2: '',
    function_l3: '',
    function_l4: ''
  });

  // Update handleApplyFilter to handle all levels
  const handleApplyFilter = (function_l1: string, function_l2: string, function_l3: string, function_l4: string) => {
    const newFilters = {
      function_l1,
      function_l2,
      function_l3,
      function_l4
    };
    setFilterValues(newFilters);
    setPage(1);
    fetchRoutineData(newFilters);
  };


  const fetchRoutineData = async (filters: Partial<FilterValues> = {}) => {
    const selectedTab = verticalTabs.find((tab) => tab.value === verticalTabValue);

    // Skip API call if "Change Management" is selected (parent tab)
    if (selectedTab?.label === "Change Management") {
      return;
    }

    setIsLoading(true);
    try {
      let sectionName = selectedTab?.label;

      // If the selected tab is a sub-tab under "Change Management", find the sub-tab's label
      if (!sectionName) {
        const changeManagementTab = verticalTabs.find((tab) => tab.label === "Change Management");
        if (changeManagementTab?.subTabs) {
          const selectedSubTab = changeManagementTab.subTabs.find((subTab) => subTab.value === verticalTabValue);
          if (selectedSubTab) {
            sectionName = selectedSubTab.label;
          }
        }
      }

      // Ensure sectionName is defined before making the API call
      if (!sectionName) {
        throw new Error("Section name is undefined");
      }

      const response = await apiService(
        "/management/filter_project_management",
        "GET",
        {
          management: management,
          section_name: sectionName,
          ...filters
        },
        undefined,
        instance as PublicClientApplication
      );

      if (response.data) {
        setRoutineData(response.data);
        setFiles(response.data);
      }
    } catch (error) {
      console.error("Error fetching routine data:", error);
      setRoutineData([]);
      setError(error instanceof Error ? error.message : "An error occurred");
      if (error instanceof Error && (error as any).response?.status === 401) {
        navigate('/login');
      }
    } finally {
      setIsLoading(false);
    }
  };
  useEffect(() => {
    fetchRoutineData();
  }, [verticalTabValue, management, navigate]);


  const handleVerticalTabChange = (event: React.SyntheticEvent, newValue: string) => {
    setVerticalTabValue(newValue);
      setFilterValues({
    function_l1: '',
    function_l2: '',
    function_l3: '',
    function_l4: ''
  });

    // Check if the selected tab is "Change Management"
    const selectedTab = verticalTabs.find((tab) => tab.value === newValue);
    if (selectedTab?.label === "Change Management") {
      // Do not make the API call for "Change Management"
      setDropdownOpen(true); // Open the dropdown for sub-tabs
    } else {
      setDropdownOpen(false); // Close the dropdown
      // The API call will be triggered by the useEffect when verticalTabValue changes
    }
  };


  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const [selectedFile, setSelectedFile] = useState<{ id: any; file_name: string, function_l1: any, current_project: any, created_at: any, updated_at: any, updated_by: any, status: any, reason_to_approve: any, reason_to_revert: any } | null>(null);

  console.log("Selected File:", selectedFile);

  const handleViewClick = (id: any, file_name: string, function_l1: any, current_project: any, created_at: any, updated_at: any, updated_by: any, status: any, reason_to_approve: any, reason_to_revert: any) => {
    setPopupOpen(true);
    setSelectedFile({ id, file_name, function_l1, current_project, created_at, status, reason_to_approve, reason_to_revert, updated_at, updated_by });
  };

  const breadcrumbs = [
    <Link key="1" color="#000" to="/">
      Home
    </Link>,
    <Link key="2" color="#000" to="/operations/management">
      Evidence Upload
    </Link>,
    <Link key="3" color="#000" to="/operations/management">
      Management
    </Link>,
    <Link key="4" color="#000" to="/">
      {management}
    </Link>,
  ];

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    console.log("Tab changed to:", newValue);
    setTabValue(newValue);
  };

  const counts = {
    All: files.length,
    Pending: files.filter((file) => file.status === 'Pending').length,
    Approved: files.filter((file) => file.status === 'Approved').length,
    Reverted: files.filter((file) => file.status === 'Reverted').length,
  };

  return (
    <>
      <ActivityBar breadcrumbs={breadcrumbs} />
      <Box sx={{ width: '100%' }}></Box>
      <SectionSlider
        sections={sections}
        sectionPaths={sectionPaths}
        highlightSection={'Project Management'}
        highlightColor="#D7A833"
        defaultColor="#666"
      />

      <ProjectManagementTabs
        tabValue={tabValue}
        question={getFileQuestions(verticalTabValue)}

        handleTabChange={handleTabChange}
        tabStatuses={tabStatuses}
        counts={counts}
        verticalTabValue={verticalTabValue}
        handleVerticalTabChange={handleVerticalTabChange}
        // verticalTabs={verticalTabs}
        isLoading={isLoading}
        routineData={routineData}
        filteredRoutineData={filteredRoutineData}
        paginatedData={paginatedData}
        handleChangePage={handleChangePage}
        itemsPerPage={itemsPerPage}
        setOpenUpload={setOpenUpload}
        getFileImagePath={getFileImagePath}
        handleViewClick={handleViewClick}
        management={management}
        verticalTabs={verticalTabs}
        verticalTabValueNew={
          verticalTabs.find(tab => tab.value === verticalTabValue)?.label ||
          verticalTabs
            .find(tab => tab.subTabs?.some(subTab => subTab.value === verticalTabValue))
            ?.subTabs?.find(subTab => subTab.value === verticalTabValue)?.label
        }
        handleApplyFilter={handleApplyFilter}
        level1={true}
        initialValues={{
          l1: filterValues.function_l1,
          l2: filterValues.function_l2,
          l3: filterValues.function_l3,
          l4: filterValues.function_l4
        }}
        functionL1={filterValues.function_l1}
        functionL2={filterValues.function_l2}
        functionL3={filterValues.function_l3}
        functionL4={filterValues.function_l4}
      />

      {/* <ProjectManagementPopup openUpload={openUpload} setOpenUpload={setOpenUpload} management={management} verticalTabValue={verticalTabs.find(tab => tab.value === verticalTabValue)?.label} /> */}
      <ProjectManagementPopup
        openUpload={openUpload}
        setOpenUpload={setOpenUpload}
        management={management}
        verticalTabValue={
          verticalTabs.find(tab => tab.value === verticalTabValue)?.label ||
          verticalTabs
            .find(tab => tab.subTabs?.some(subTab => subTab.value === verticalTabValue))
            ?.subTabs?.find(subTab => subTab.value === verticalTabValue)?.label
        }
        onSuccess={fetchRoutineData}
      />

      {isPopupOpen && selectedFile && (
        <>
          {isManager && (
            <ProjectManagementFileView
              onClose={handleClosePopup}
              open={true}
              name={selectedFile.file_name}
              id={selectedFile.id}
              functionl1={selectedFile.function_l1}
              current_project={selectedFile.current_project}
              date={selectedFile.updated_at}
              updatedBy={selectedFile.updated_by}
              sectionTitle={verticalTabs.find(tab => tab.value === verticalTabValue)?.label || ''}
              userDetail={true}
              apiPath={`/management/project_management/${selectedFile.id}`}
              getFileImagePath={getFileImagePath}
            />
          )}

          {isAdmin && (
            <ProjectManagementAdminFileView
              onClose={handleClosePopup}
              open={true}
              name={selectedFile.file_name}
              id={selectedFile.id}
              functionl1={selectedFile.function_l1}
              current_project={selectedFile.current_project}

              date={selectedFile.updated_at}
              updatedBy={selectedFile.updated_by}
              status={selectedFile.status}
              reason_to_approve={selectedFile.reason_to_approve}
              reason_to_revert={selectedFile.reason_to_revert}
              sectionTitle={verticalTabs.find(tab => tab.value === verticalTabValue)?.label || ''}
              apiPath={`/management/project_management/${selectedFile.id}`}
              statusApiPath="/management/project_management_status"
              getFileImagePath={getFileImagePath}
              onSuccess={fetchRoutineData} // Add this line

            />
          )}
        </>
      )}
    </>
  );
};

export default ProjectManagementUpload;